<template>
  <Teleport
    to="body"
    :disabled="isWebComponent"
  >
    <div
      v-if="modalVisible"
      class="modal"
      :class="[props.modalClass, { 'modal--webcomponent': isWebComponent }]"
    >
      <div
        class="modal__dialog"
        ref="modal"
      >
        <div
          v-if="$slots.header"
          class="modal__header"
        >
          <BasicText
            size="h4"
            weight="bold"
            color="white"
            nbMaxLines="2"
          >
            <slot name="header"></slot>
          </BasicText>

          <BasicIcon
            v-if="closable"
            name="close-square"
            @click="closeModal"
            pointer
          />
        </div>

        <div
          class="modal__content"
          :class="{ 'modal__content--no-scroll': noContentScroll }"
        >
          <BasicIcon
            v-if="closable && !$slots.header"
            name="close-square"
            @click="closeModal"
            pointer
          />
          <slot name="content"></slot>
        </div>

        <div
          v-if="$slots.actions"
          class="modal__actions"
        >
          <slot name="actions"></slot>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
  import { ref, onMounted, onUnmounted, provide, computed } from 'vue'
  import { useHandleClickOutside } from '@/features/interface/composables/useHandleClickOutside'
  import { PAUSE_CLICK_OUTSIDE } from './ModalComponent.symbols'
  import { useWebComponentNode } from '@/features/interface/composables/useWebComponentNode'

  const webComponentNode = useWebComponentNode()
  const isWebComponent = computed(() => !!webComponentNode.shadowRoot)

  interface ModalProps {
    closable?: boolean
    noContentScroll?: boolean
    modalClass?: string | string[] | Record<string, boolean>
  }

  const props = withDefaults(defineProps<ModalProps>(), {
    closable: true,
    noContentScroll: false,
    modalClass: '',
  })

  const modal = ref()
  const modalVisible = defineModel<boolean>()
  const emit = defineEmits(['close'])

  function closeModal() {
    if (props.closable !== false) {
      modalVisible.value = false
      emit('close')
    }
  }

  const { pauseClickOutside } = useHandleClickOutside(modal, () => closeModal())

  provide(PAUSE_CLICK_OUTSIDE, pauseClickOutside)

  function handleEscapeKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && modalVisible.value && props.closable) {
      closeModal()
    }
  }

  onMounted(() => {
    window.addEventListener('keydown', handleEscapeKeydown)
  })

  onUnmounted(() => {
    window.removeEventListener('keydown', handleEscapeKeydown)
  })
</script>

<style scoped>
  @import './ModalComponent.less';
</style>
